// The Tides panel (index.html #pop-out-box) and the rail's Tides glance chip.
// Rules live in tidePanelCore.js; this file fetches NOAA and draws.
//
// One NOAA call per viewed day: hilo predictions from the day before to the day after, in the
// station's local time, so the curve runs through midnight and every high/low has its height.
// The rail chip always shows today's next turn, whatever day the panel is looking at.
import { tideStation, marinaTimeZone, NO_LOCATION_MSG } from './marinaLocation';
import {
  WORK_START, WORK_END, parseHiLo, dayTurns, dayCurve, tideState, tideLevel, heightAt,
  timeLabel, durationLabel, feet, shiftDay, marinaNow,
} from './tidePanelCore';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const OFFLINE_MSG = "You're offline, or NOAA didn't respond. Showing nothing rather than stale times.";
const CACHE_MS = 30 * 60 * 1000;

const $ = id => document.getElementById(id);
const cache = new Map(); // `${station}|${day}` → { list, at }
let viewDay = null;
let seq = 0;

const today = () => marinaNow(marinaTimeZone());

async function loadDay(station, day) {
  const key = `${station}|${day}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.list;
  const d = s => s.replace(/-/g, '');
  const url = 'https://api.tidesandcurrents.noaa.gov/api/prod/datagetter'
    + `?begin_date=${d(shiftDay(day, -1))}&end_date=${d(shiftDay(day, 1))}&time_zone=lst_ldt&interval=hilo&datum=MLLW&format=json&units=english`
    + `&station=${encodeURIComponent(station)}&product=predictions&application=client-side-js`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(res.statusText);
  const data = await res.json();
  if (!Array.isArray(data.predictions)) throw new Error(data.error?.message || 'Invalid tide response');
  const list = parseHiLo(data.predictions);
  cache.set(key, { list, at: Date.now() });
  return list;
}

function showError(message) {
  $('tide-error-body').textContent = message;
  $('tide-error').style.display = 'flex';
  for (const id of ['tide-now', 'tide-chart', 'tide-values', 'tide-caption']) $(id).style.display = 'none';
}

function hideError() {
  $('tide-error').style.display = 'none';
  for (const id of ['tide-chart', 'tide-values', 'tide-caption']) $(id).style.display = '';
}

function renderDate(day) {
  const [y, m, d] = day.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  $('tide-day').textContent = DAYS[dt.getDay()];
  $('tide-full-date').textContent = `${MONTHS[m - 1]} ${d}, ${y}`;
  $('tideDateSelect').value = day;
  $('tide-today-btn').hidden = day === today().day;
}

const tileTime = min => timeLabel(min).replace(/ (AM|PM)$/, ' <span class="tide-ampm">$1</span>');

function renderTiles(turns, now) {
  const next = now ? turns.find(t => t.min > now.min) : null;
  $('tide-values').innerHTML = turns.map(t => `
    <div class="tide-tile ${t.type === 'H' ? 'tide-tile-high' : 'tide-tile-low'}${t.when !== 'work' ? ' is-off-hours' : ''}${t === next ? ' is-next' : ''}">
      <div class="tide-tile-label">${t.type === 'H' ? '&#9650; HIGH' : '&#9660; LOW'}${t === next ? ' · NEXT' : ''}</div>
      <div class="tide-tile-value">${tileTime(t.min)}</div>
      <div class="tide-tile-height">${feet(t.v)}${t.when === 'before' ? ' · before work' : t.when === 'after' ? ' · after work' : ''}</div>
    </div>`).join('');
}

function renderNow(list, now) {
  const el = $('tide-now');
  const s = now && tideState(list, now.day, now.min);
  if (!s) { el.style.display = 'none'; return; }
  el.style.display = '';
  el.className = `tide-now ${s.rising ? 'is-rising' : 'is-falling'}`;
  el.innerHTML = `<span class="tide-now-dir">${s.rising ? '&#9650; Rising' : '&#9660; Falling'}</span>`
    + `<span class="tide-now-height">${feet(s.height)} now</span>`
    + `<span class="tide-now-next">${s.next.type === 'H' ? 'High' : 'Low'} ${feet(s.next.v)} in ${durationLabel(s.next.inMin)}</span>`;
}

// The day's curve: shaded working hours, a dot per high/low, a "now" line on today.
function renderChart(list, day, now) {
  const W = 320, H = 92, TOP = 10, BOT = 18;
  const pts = dayCurve(list, day, 10);
  if (pts.length < 2) { $('tide-chart').innerHTML = ''; return; }
  const vs = pts.map(p => p.v);
  const lo = Math.min(...vs) - 0.3, hi = Math.max(...vs) + 0.3;
  const x = min => (min / 1440) * W;
  const y = v => TOP + (1 - (v - lo) / (hi - lo)) * (H - TOP - BOT);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.min).toFixed(1)} ${y(p.v).toFixed(1)}`).join('');
  const base = H - BOT;
  const area = `${line}L${x(pts[pts.length - 1].min).toFixed(1)} ${base}L${x(pts[0].min).toFixed(1)} ${base}Z`;
  const ticks = [[0, '12a'], [360, '6a'], [720, '12p'], [1080, '6p'], [1440, '12a']]
    .map(([m, l], i, a) => `<text x="${x(m)}" y="${H - 4}" text-anchor="${i === 0 ? 'start' : i === a.length - 1 ? 'end' : 'middle'}">${l}</text>`).join('');
  const dots = dayTurns(list, day).map(t => `<circle class="${t.type === 'H' ? 'tc-high' : 'tc-low'}" cx="${x(t.min).toFixed(1)}" cy="${y(t.v).toFixed(1)}" r="3.2"><title>${t.type === 'H' ? 'High' : 'Low'} ${timeLabel(t.min)} · ${feet(t.v)}</title></circle>`).join('');
  let nowMark = '';
  if (now && now.day === day) {
    const hv = heightAt(list, day, now.min);
    if (hv != null) nowMark = `<line class="tc-now" x1="${x(now.min).toFixed(1)}" x2="${x(now.min).toFixed(1)}" y1="${TOP - 4}" y2="${base}"/><circle class="tc-now-dot" cx="${x(now.min).toFixed(1)}" cy="${y(hv).toFixed(1)}" r="3.6"/>`;
  }
  $('tide-chart').innerHTML = `
    <svg viewBox="0 0 ${W} ${H}" role="img" aria-label="Tide height through the day">
      <defs><linearGradient id="tide-chart-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0F8C82" stop-opacity=".28"/><stop offset="1" stop-color="#0F8C82" stop-opacity=".03"/></linearGradient></defs>
      <rect class="tc-work" x="${x(WORK_START)}" y="${TOP - 4}" width="${x(WORK_END) - x(WORK_START)}" height="${base - TOP + 4}" rx="4"/>
      <path d="${area}" fill="url(#tide-chart-fill)"/>
      <path class="tc-line" d="${line}"/>
      <line class="tc-base" x1="0" x2="${W}" y1="${base}" y2="${base}"/>
      ${nowMark}${dots}
      <g class="tc-ticks">${ticks}</g>
    </svg>`;
}

// The water at the bottom sits higher at high tide and swells faster while the tide runs.
function renderWaves(list, now) {
  const el = document.querySelector('.tide-waves');
  if (!el) return;
  const s = now && tideState(list, now.day, now.min);
  const level = s ? tideLevel(list, now.day, s.height) : 0.5;
  el.style.setProperty('--tide-drop', `${((1 - level) * 16).toFixed(1)}px`);
  const run = s ? Math.min(1, Math.abs(s.rate) / 2) : 0; // ~2 ft/h is a strong run
  el.style.setProperty('--swell-dur', `${(7 - run * 2.5).toFixed(2)}s`);
}

// Today's next high or low on the rail chip, e.g. "▲10:42"; the day's turns in its tooltip.
function renderRail(list, now) {
  const val = $('rail-tide-value');
  if (!val) return;
  const s = tideState(list, now.day, now.min);
  const turns = dayTurns(list, now.day);
  val.textContent = s ? `${s.next.type === 'H' ? '▲' : '▼'}${timeLabel(s.next.min).replace(/ [AP]M$/, '')}` : '';
  val.closest('button')?.setAttribute('title', turns.map(t => `${t.type === 'H' ? 'High' : 'Low'} ${timeLabel(t.min)} (${feet(t.v)})`).join(' · '));
}

async function show(day) {
  viewDay = day;
  const mine = ++seq;
  renderDate(day);
  if (!navigator.onLine) { showError(OFFLINE_MSG); return; }
  const station = await tideStation();
  document.querySelectorAll('.dock-panel-eyebrow[data-tide-station]').forEach(el => { el.textContent = station ? `NOAA ${station}` : 'NOAA'; });
  if (mine !== seq) return;
  if (!station) { showError(NO_LOCATION_MSG); return; }
  let list;
  try {
    list = await loadDay(station, day);
  } catch (e) {
    console.error('Failed to fetch tide data:', e);
    if (mine === seq) showError(OFFLINE_MSG);
    return;
  }
  if (mine !== seq) return; // a later day was asked for while this one loaded
  const turns = dayTurns(list, day);
  if (!turns.length) { showError('NOAA has no high or low tide predictions for this date.'); return; }
  hideError();
  const now = today();
  const isToday = now.day === day;
  renderNow(list, isToday ? now : null);
  renderTiles(turns, isToday ? now : null);
  renderChart(list, day, now);
  renderWaves(list, isToday ? now : null);
  if (isToday) renderRail(list, now);
}

// The rail chip on its own (the panel may be on another day).
async function refreshRail() {
  try {
    const station = await tideStation();
    if (!station || !navigator.onLine) return;
    const now = today();
    renderRail(await loadDay(station, now.day), now);
  } catch (e) { /* the panel shows the error when opened */ }
}

export function initTidePanel() {
  const panel = $('pop-out-box');
  if (!panel) return;
  $('open-btn')?.addEventListener('click', () => panel.classList.toggle('open'));
  $('close-btn')?.addEventListener('click', () => panel.classList.remove('open'));
  $('tide-date-next').addEventListener('click', () => show(shiftDay(viewDay, 1)));
  $('tide-date-previous').addEventListener('click', () => show(shiftDay(viewDay, -1)));
  $('tide-today-btn').addEventListener('click', () => show(today().day));
  $('tide-retry-btn')?.addEventListener('click', () => { cache.clear(); show(viewDay); });
  const input = $('tideDateSelect');
  $('tide-date-pick').addEventListener('click', () => { try { input.showPicker(); } catch { input.focus(); } });
  input.addEventListener('change', () => { if (input.value) show(input.value); });

  show(today().day);
  // Keep "now", the next tide and the water level current; NOAA is only asked again past the cache.
  setInterval(() => {
    const now = today();
    if (viewDay !== now.day && panel.classList.contains('open')) refreshRail(); // leave the day they're looking at
    else show(now.day);
  }, 5 * 60 * 1000);
}
